import SettingConfig from '@module/config/setting';
import { getCountryCodeFromLang } from '@module/helpers/chat/formatCountryNamesByLocale';
import useChatMessageStore, { type SponItem } from '@module/stores/useChatMessageStore';
import { type ChatUIAffectingMessage, isChatMessage } from '@module/types/centrifuge/chatTypes';
import type { ParsedCentrifugeMessage, ParsedPublicMessage } from '@module/types/centrifuge/messageTypes';
import { isFreezeOnMessage } from '@module/types/centrifuge/public-message/freezeMessageTypes';
import {
  isNormalChatMessage,
  type NormalChatMessage,
} from '@module/types/centrifuge/public-message/normalChatMessageTypes';
import {
  type ItemCoinMessage,
  isItemCoinMessage,
  isSponCoinMessage,
  type SponCoinMessage,
} from '@module/types/centrifuge/public-message/sponCoinMessageTypes';
import type { MessageHandler, MessageHandlerAction, MessageHandlerContext } from '../types';

/**
 * 일반 채팅 메시지 처리
 * - 성인 컨텐츠 필터링
 * - 사이트별 필터링
 * - FreezeOn (별도 핸들러에서 처리)
 */
export class ChatMessageHandler implements MessageHandler {
  constructor(private customAction?: MessageHandlerAction<ChatUIAffectingMessage>) {}

  canHandle(payload: ParsedCentrifugeMessage): boolean {
    return isChatMessage(payload) && !isFreezeOnMessage(payload);
  }

  handle(payload: ChatUIAffectingMessage, context: MessageHandlerContext): void {
    // null/undefined 이면 skip
    if (this.customAction === null || this.customAction === undefined) return;

    // 커스텀 action 실행
    if (typeof this.customAction === 'function') {
      this.customAction(payload, context);
      return;
    }

    // 기본 동작 (USE_DEFAULT)
    if (
      (isNormalChatMessage(payload) && payload.data.type !== 'bj' && payload.data.type !== 'support') ||
      isSponCoinMessage(payload) ||
      isItemCoinMessage(payload)
    ) {
      // blockBaseLocale는 채팅 차단 기준 국가
      const { isBlocked } = this.getChatBlockCondition({
        context,
        payload,
      });

      const isCoinMessage = isSponCoinMessage(payload) || isItemCoinMessage(payload);
      // 일반 메세지 차단 설정
      if (isNormalChatMessage(payload) && isBlocked) return;

      // 후원 메세지 차단 설정
      if (isCoinMessage && isBlocked) {
        this.processCoinMessage({
          payload,
          context,
        });
        return;
      }
    }

    context.processChatMessage(payload);
  }

  private getChatBlockCondition({
    context,
    payload,
  }: {
    context: MessageHandlerContext;
    payload: SponCoinMessage | ItemCoinMessage | NormalChatMessage;
  }) {
    const { blockedLocales, fan } = context.playInfo ?? {};
    const { lang } = isNormalChatMessage(payload) ? payload.data : (payload.data.message.heartMessage ?? {});

    // 관리자 채팅 숨김 설정
    const isLocaleBlocked = lang?.some((code) => blockedLocales?.includes(getCountryCodeFromLang(code)));
    // 관리자 채팅 설정은 BJ가 아닌 경우에만 적용
    const isAdminBlocked = !fan?.isRoomMaster && isLocaleBlocked;

    // 글로벌 채팅 숨김 개별 설정
    const isGlobalHidden = context.isGlobalChatVisible === false && !lang?.includes(SettingConfig.LANG);
    const isBlocked = isAdminBlocked || isGlobalHidden;

    return {
      isBlocked,
    };
  }

  private processCoinMessage({
    payload,
    context,
  }: {
    payload: SponCoinMessage | ItemCoinMessage;
    context: MessageHandlerContext;
  }) {
    const { heartMessage: _heartMessage, ...restMessage } = payload.data.message;

    const finalPayload = {
      ...payload,
      data: {
        ...payload.data,
        message: restMessage,
      },
    } as SponItem;

    context.processChatMessage(finalPayload);
  }
}

/**
 * 후원 메시지 별도 저장 처리
 */
export class SponMessageHandler implements MessageHandler {
  constructor(private customAction?: MessageHandlerAction<SponItem>) {}

  canHandle(payload: ParsedPublicMessage): boolean {
    return isSponCoinMessage(payload) || isItemCoinMessage(payload);
  }

  handle(payload: SponItem, context: MessageHandlerContext): void {
    // null/undefined 이면 skip
    if (this.customAction === null || this.customAction === undefined) return;

    // 커스텀 action 실행
    if (typeof this.customAction === 'function') {
      this.customAction(payload, context);
      return;
    }

    // 기본 동작 (USE_DEFAULT)
    useChatMessageStore.setState({
      sponMessages: [...useChatMessageStore.getState().sponMessages, payload],
    });
  }
}
